const SSE_EVENT_NAME = 'nfse.status.updated';
const DEFAULT_RETRY_BASE_DELAY_MS = 1_000;
const DEFAULT_RETRY_MAX_DELAY_MS = 30_000;
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
function parseEvent(frame) {
    if (frame.eventName !== SSE_EVENT_NAME || frame.notificationId === '')
        return null;
    try {
        const data = JSON.parse(frame.data);
        if (!isRecord(data) || data.domain !== 'nfse' || data.event !== 'status.updated' || !isRecord(data.payload)) {
            return null;
        }
        const { resourceType, resourceId, status, occurredAt, errorMessage } = data.payload;
        if (!isBillingRealtimeResourceType(resourceType) ||
            typeof resourceId !== 'string' ||
            typeof status !== 'string' ||
            typeof occurredAt !== 'string' ||
            Number.isNaN(Date.parse(occurredAt)) ||
            (errorMessage !== null && typeof errorMessage !== 'string')) {
            return null;
        }
        return {
            notificationId: frame.notificationId,
            resourceType,
            resourceId,
            status,
            occurredAt,
            errorMessage,
        };
    }
    catch {
        return null;
    }
}
function isBillingRealtimeResourceType(value) {
    return value === 'nfse' || value === 'billing';
}
function parseFrames(buffer) {
    const chunks = buffer.split(/\r?\n\r?\n/);
    const remainder = chunks.pop() ?? '';
    const frames = chunks.flatMap(chunk => {
        let eventName = '';
        let notificationId = '';
        const dataLines = [];
        for (const line of chunk.split(/\r?\n/)) {
            if (line.startsWith('event:'))
                eventName = line.slice('event:'.length).trim();
            if (line.startsWith('id:'))
                notificationId = line.slice('id:'.length).trim();
            if (line.startsWith('data:'))
                dataLines.push(line.slice('data:'.length).trimStart());
        }
        return dataLines.length > 0 ? [{ eventName, notificationId, data: dataLines.join('\n') }] : [];
    });
    return { frames, remainder };
}
export class BillingRealtimeClient {
    fetchFn;
    onUnauthorized;
    retryBaseDelayMs;
    retryMaxDelayMs;
    random;
    listeners = new Set();
    retainers = 0;
    statusListeners = new Set();
    context = null;
    controller = null;
    retryTimer = null;
    isStoppingConnection = false;
    restartRequested = false;
    generation = 0;
    attempts = 0;
    status = 'unavailable';
    constructor(options = {}) {
        this.fetchFn = options.fetchFn ?? ((input, init) => fetch(input, init));
        this.onUnauthorized = options.onUnauthorized;
        this.retryBaseDelayMs = options.retryBaseDelayMs ?? DEFAULT_RETRY_BASE_DELAY_MS;
        this.retryMaxDelayMs = options.retryMaxDelayMs ?? DEFAULT_RETRY_MAX_DELAY_MS;
        this.random = options.random ?? Math.random;
    }
    subscribe(context, listener) {
        this.setContext(context);
        this.listeners.add(listener);
        this.startIfNeeded();
        let isUnsubscribed = false;
        return () => {
            if (isUnsubscribed)
                return;
            isUnsubscribed = true;
            this.listeners.delete(listener);
            if (!this.hasActiveConsumers())
                this.stop();
        };
    }
    retain(context) {
        this.setContext(context);
        this.retainers += 1;
        this.startIfNeeded();
        let isReleased = false;
        return () => {
            if (isReleased)
                return;
            isReleased = true;
            this.retainers -= 1;
            if (!this.hasActiveConsumers())
                this.stop();
        };
    }
    subscribeStatus(listener) {
        this.statusListeners.add(listener);
        listener(this.status);
        return () => this.statusListeners.delete(listener);
    }
    setContext(context) {
        const isSameConnection = this.context?.fingerprint === context?.fingerprint;
        this.context = context;
        if (isSameConnection)
            return;
        this.restart();
    }
    destroy() {
        this.listeners.clear();
        this.retainers = 0;
        this.statusListeners.clear();
        this.context = null;
        this.stop();
    }
    restart() {
        this.stop();
        this.startIfNeeded();
    }
    startIfNeeded() {
        if (!this.context || !this.hasActiveConsumers())
            return;
        if (this.controller) {
            if (this.isStoppingConnection)
                this.restartRequested = true;
            return;
        }
        this.restartRequested = false;
        const generation = this.generation;
        this.controller = new AbortController();
        void this.connect(generation, this.context, this.controller);
    }
    stop() {
        this.generation += 1;
        if (this.controller) {
            this.isStoppingConnection = true;
            this.controller.abort();
        }
        else {
            this.isStoppingConnection = false;
        }
        if (this.retryTimer)
            clearTimeout(this.retryTimer);
        this.retryTimer = null;
        this.attempts = 0;
        this.setStatus('unavailable');
    }
    async connect(generation, context, controller) {
        try {
            const response = await this.fetchFn(context.url, {
                method: 'GET',
                headers: { ...context.headers, Accept: 'text/event-stream' },
                signal: controller.signal,
            });
            if (!this.isCurrent(generation, context, controller))
                return;
            if (!response.ok || !response.body) {
                console.error('[BillingRealtime] SSE connection was rejected', {
                    url: context.url,
                    status: response.status,
                    statusText: response.statusText,
                    hasStream: Boolean(response.body),
                });
                if (response.status === 401) {
                    await this.refreshUnauthorizedSession();
                    return;
                }
                this.scheduleRetry(generation, context);
                return;
            }
            this.attempts = 0;
            this.setStatus('connected');
            await this.consume(response.body, generation, context, controller);
            if (this.isCurrent(generation, context, controller))
                this.scheduleRetry(generation, context);
        }
        catch (error) {
            if (!this.isCurrent(generation, context, controller))
                return;
            console.error('[BillingRealtime] SSE connection request failed', {
                url: context.url,
                error: error instanceof Error ? error.message : String(error),
            });
            this.scheduleRetry(generation, context);
        }
        finally {
            if (this.controller === controller) {
                this.controller = null;
                this.isStoppingConnection = false;
                if (this.restartRequested)
                    this.startIfNeeded();
            }
        }
    }
    async refreshUnauthorizedSession() {
        if (!this.onUnauthorized) {
            this.stop();
            return;
        }
        this.setStatus('refreshing-session');
        const refreshed = await this.onUnauthorized().catch(() => false);
        if (!refreshed)
            this.stop();
    }
    async consume(stream, generation, context, controller) {
        const reader = stream.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        try {
            while (this.isCurrent(generation, context, controller)) {
                const { done, value } = await reader.read();
                if (done || !this.isCurrent(generation, context, controller))
                    return;
                const parsed = parseFrames(buffer + decoder.decode(value, { stream: true }));
                buffer = parsed.remainder;
                for (const frame of parsed.frames) {
                    const event = parseEvent(frame);
                    if (event && this.isCurrent(generation, context, controller))
                        this.listeners.forEach(listener => listener(event));
                }
            }
        }
        finally {
            reader.releaseLock();
        }
    }
    scheduleRetry(generation, context) {
        if (!this.isCurrent(generation, context, null) || !this.hasActiveConsumers())
            return;
        this.controller = null;
        this.attempts += 1;
        this.setStatus('reconnecting');
        const exponentialDelay = Math.min(this.retryBaseDelayMs * 2 ** (this.attempts - 1), this.retryMaxDelayMs);
        const delay = Math.round(exponentialDelay * (0.5 + this.random()));
        this.retryTimer = setTimeout(() => {
            this.retryTimer = null;
            if (!this.isCurrent(generation, context, null))
                return;
            this.startIfNeeded();
        }, delay);
    }
    isCurrent(generation, context, controller) {
        return (this.generation === generation &&
            this.context?.fingerprint === context.fingerprint &&
            (controller === null || this.controller === controller));
    }
    hasActiveConsumers() {
        return this.listeners.size > 0 || this.retainers > 0;
    }
    setStatus(status) {
        if (this.status === status)
            return;
        this.status = status;
        this.statusListeners.forEach(listener => listener(status));
    }
}
